/**
 * Redux reducer for order close confirmation
 *
 * State structure:
 * - declinedOrders: string[] - Order IDs where user said "No, keep open"
 * - pendingConfirmations: notas pendientes del aviso, UNA por orden (OT,
 *   campaña o recorrido), la más reciente al final. Antes había una sola y
 *   terminar dos órdenes seguidas perdía la primera (spec
 *   2026-10-12-aviso-cierre-recorrido § 12).
 */

import {
  ORDER_CLOSE_SET_PENDING,
  ORDER_CLOSE_DECLINE,
  ORDER_CLOSE_CLEAR_PENDING,
  ORDER_CLOSE_REMOVE_DECLINED,
  ORDER_CLOSE_RESET,
} from '../../actions/ui/orderCloseConfirmation';

export interface PendingCloseConfirmation {
  parentOrderId: string;
  parentRoid?: string; // ROID for display in modal link
  triggeredAt: number;
}

export interface OrderCloseConfirmationState {
  // Orders where user already said "No, keep open"
  declinedOrders: string[];
  // Una nota por orden, la más reciente al final
  pendingConfirmations: PendingCloseConfirmation[];
}

export const initialOrderCloseState: OrderCloseConfirmationState = {
  declinedOrders: [],
  pendingConfirmations: [],
};

type ActionType = {
  type: string;
  payload?: {
    parentOrderId?: string;
    parentRoid?: string;
    triggeredAt?: number;
  };
};

const withoutParent = (
  pending: PendingCloseConfirmation[],
  parentOrderId: string
) => pending.filter((p) => p.parentOrderId !== parentOrderId);

export default function orderCloseConfirmation(
  state = initialOrderCloseState,
  { type, payload }: ActionType
): OrderCloseConfirmationState {
  switch (type) {
    case ORDER_CLOSE_SET_PENDING: {
      // Only set if this order was NOT already declined
      if (
        !payload?.parentOrderId ||
        state.declinedOrders.includes(payload.parentOrderId)
      ) {
        return state;
      }
      // La misma orden no se duplica: se actualiza y pasa al final.
      return {
        ...state,
        pendingConfirmations: [
          ...withoutParent(state.pendingConfirmations, payload.parentOrderId),
          {
            parentOrderId: payload.parentOrderId,
            parentRoid: payload.parentRoid,
            triggeredAt: payload.triggeredAt || Date.now(),
          },
        ],
      };
    }

    case ORDER_CLOSE_DECLINE: {
      if (!payload?.parentOrderId) return state;
      // Add to declined list and clear ITS pending note
      const newDeclined = state.declinedOrders.includes(payload.parentOrderId)
        ? state.declinedOrders
        : [...state.declinedOrders, payload.parentOrderId];
      return {
        ...state,
        declinedOrders: newDeclined,
        pendingConfirmations: withoutParent(
          state.pendingConfirmations,
          payload.parentOrderId
        ),
      };
    }

    case ORDER_CLOSE_CLEAR_PENDING:
      return {
        ...state,
        pendingConfirmations: payload?.parentOrderId
          ? withoutParent(state.pendingConfirmations, payload.parentOrderId)
          : [],
      };

    case ORDER_CLOSE_REMOVE_DECLINED: {
      if (!payload?.parentOrderId) return state;
      return {
        ...state,
        declinedOrders: state.declinedOrders.filter(
          (id) => id !== payload.parentOrderId
        ),
      };
    }

    case ORDER_CLOSE_RESET:
      return {
        ...initialOrderCloseState,
      };

    default:
      return state;
  }
}
